import { computed, type ComputedRef, reactive, type Ref, toRefs } from "vue";

import type { Account } from "./account.types";

interface AccountStoreState {
  account: Account | null;
}

/**
 * Global reactive authenticated account store
 *
 * NOTE: Will not work with SSR as it will be shared across requests!
 */
const accountStore = reactive<AccountStoreState>({
  account: null,
});

interface AccountStore {
  /**
   * Authenticated account (viewer)
   *
   * NOTE: Should remain unset after login and remain set after logout, as both trigger reloads that
   *         will properly set the auth state after the reload (this avoids flickers before reload).
   */
  account: Readonly<Ref<Account | null>>;
  /** Whether viewer is authenticated (computed from `account`) */
  authenticated: ComputedRef<boolean>;
  clearAccount: () => void;
  setAccount: (account: Account) => void;
}

export const useAuthAccount = (): AccountStore => {
  const clearAccount = () => {
    accountStore.account = null;
  };

  const setAccount = (account: Account) => {
    accountStore.account = account;
  };

  const storeStateRefs = toRefs(accountStore);

  return {
    // TODO: Consider enforcing as readonly (unless 'Readonly' func return type is sufficient?)
    account: storeStateRefs.account,
    authenticated: computed(() => !!accountStore.account),
    clearAccount,
    setAccount,
  };
};
